import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { CreatingSearchHHForm } from "../constans";
import { SortableEditableList } from "./SortableEditableList";
import { DragOverlay } from "@dnd-kit/core";
import { useEffect, useState } from "react";

import styles from "./RequirementsDndLists.module.scss";

type TListName = "mandatory" | "priority" | "optional";

const isListName = (value: string): value is TListName => {
  return value === "mandatory" || value === "priority" || value === "optional";
};

interface Props {
  control: Control<CreatingSearchHHForm>;
  isDisabled?: boolean;
  mandatoryError?: string;
}

const LISTS: {
  name: TListName;
  title: string;
  placeholder: string;
}[] = [
  {
    name: "mandatory",
    title: "Обязательные требования:",
    placeholder: "Вставьте описание",
  },
  {
    name: "priority",
    title: "Приоритетные требования:",
    placeholder: "Вставьте описание",
  },
  {
    name: "optional",
    title: "Опциональные требования:",
    placeholder: "Вставьте описание",
  },
];

const getItemDndId = (listName: TListName, uid: string) =>
  ` ${listName}:${uid}`;

const getContainerDndId = (listName: TListName) => `container:${listName}`;

const parseDndId = (id: string) => {
  const normalizedId = id.trim();
  const [firstPart, ...rest] = normalizedId.split(":");
  const secondPart = rest.join(":");

  if (firstPart === "container") {
    const listName = secondPart.trim();

    if (!isListName(listName)) {
      return null;
    }

    return {
      type: "container" as const,
      listName,
      uid: null,
    };
  }

  const listName = firstPart.trim();
  const uid = secondPart.trim();

  if (!isListName(listName) || !uid) {
    return null;
  }

  return {
    type: "item" as const,
    listName,
    uid,
  };
};
export const RequirementsDndLists = ({
  control,
  isDisabled,
  mandatoryError,
}: Props) => {
  const { getValues, trigger } = useFormContext<CreatingSearchHHForm>();
  const [activeItemValue, setActiveItemValue] = useState<string | null>(null);

  const mandatoryValue = useWatch({
    control,
    name: "mandatory",
  });

  useEffect(() => {
    trigger("mandatory");
  }, [mandatoryValue, trigger]);

  const mandatoryArray = useFieldArray({
    control,
    name: "mandatory",
  });
  const priorityArray = useFieldArray({
    control,
    name: "priority",
  });
  const optionalArray = useFieldArray({
    control,
    name: "optional",
  });
  const fieldArrays = {
    mandatory: mandatoryArray,
    priority: priorityArray,
    optional: optionalArray,
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || isDisabled) return;

    const activeData = parseDndId(String(active.id));
    const overData = parseDndId(String(over.id));

    if (!activeData || !overData) return;
    if (activeData.type !== "item") return;

    const sourceListName = activeData.listName;
    const targetListName = overData.listName;

    const sourceArray = fieldArrays[sourceListName];
    const targetArray = fieldArrays[targetListName];

    if (!sourceArray || !targetArray) return;

    const sourceIndex = sourceArray.fields.findIndex(
      (field) => field.uid === activeData.uid,
    );

    if (sourceIndex === -1) return;

    const targetIndex =
      overData.type === "container"
        ? targetArray.fields.length
        : targetArray.fields.findIndex((field) => field.uid === overData.uid);

    if (targetIndex === -1) return;

    if (sourceListName === targetListName) {
      if (sourceIndex === targetIndex) return;

      sourceArray.move(sourceIndex, targetIndex);
      trigger(["mandatory", "priority", "optional"]);
      return;
    }

    const movedItem = getValues(`${sourceListName}.${sourceIndex}`);

    sourceArray.remove(sourceIndex);
    targetArray.insert(targetIndex, movedItem);

    trigger(["mandatory", "priority", "optional"]);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => {
        const activeData = parseDndId(String(active.id));

        if (!activeData || activeData.type !== "item") return;

        const sourceArray = fieldArrays[activeData.listName];

        const sourceIndex = sourceArray.fields.findIndex(
          (field) => field.uid === activeData.uid,
        );

        if (sourceIndex === -1) return;

        const item = getValues(`${activeData.listName}.${sourceIndex}`);

        setActiveItemValue(item?.value ?? "");
      }}
      onDragEnd={(event) => {
        handleDragEnd(event);
        setActiveItemValue(null);
      }}
      onDragCancel={() => {
        setActiveItemValue(null);
      }}
    >
      {LISTS.map((list) => {
        const fieldArray = fieldArrays[list.name];

        return (
          <SortableContext
            key={list.name}
            items={fieldArray.fields.map((field) =>
              getItemDndId(list.name, field.uid),
            )}
            strategy={verticalListSortingStrategy}
          >
            <SortableEditableList
              control={control}
              name={list.name}
              title={list.title}
              placeholder={list.placeholder}
              fields={fieldArray.fields}
              append={fieldArray.append}
              remove={fieldArray.remove}
              isDisabled={isDisabled}
              getItemDndId={getItemDndId}
              containerDndId={getContainerDndId(list.name)}
              error={list.name === "mandatory" ? mandatoryError : undefined}
            />
          </SortableContext>
        );
      })}
      <DragOverlay>
        {activeItemValue !== null && (
          <div className={styles.dragOverlay}>
            {activeItemValue || "Пустое значение"}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
